// src/pages/admin/PracticeMatchGroup.jsx
import DepositMatchRow from "./DepositMatchRow";
import { formatCad, formatSignedCad } from "./depositMatchUtils";

const AVATAR_COLORS = [
  "bg-palette-indigo-badge",
  "bg-palette-amber-heading",
  "bg-palette-emerald-badge",
  "bg-palette-slate-badge",
  "bg-palette-indigo-heading",
  "bg-palette-emerald-hover",
];

const initials = (name = "") =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");

// Stable colour per practice so the avatar doesn't shift between renders.
const avatarColor = (name = "") =>
  AVATAR_COLORS[[...name].reduce((h, c) => h + c.charCodeAt(0), 0) % AVATAR_COLORS.length];

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

function Stat({ label, value, sub, tone = "default" }) {
  const valueClass =
    tone === "good"
      ? "text-palette-emerald-hover"
      : tone === "warn"
        ? "text-palette-amber-primary"
        : "text-palette-slate-primary";
  return (
    <div className="flex min-w-0 flex-[1_1_120px] flex-col gap-px py-0.5">
      <span className="whitespace-nowrap text-[11px] font-semibold uppercase tracking-wider text-palette-slate-placeholder">
        {label}
      </span>
      <span className={`whitespace-nowrap text-[15px] font-semibold tabular-nums ${valueClass}`}>
        {value}
      </span>
      <span className="truncate text-[11.5px] text-palette-slate-placeholder">{sub}</span>
    </div>
  );
}

function Chevron({ open }) {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`flex-none text-palette-slate-placeholder transition-transform ${
        open ? "" : "-rotate-90"
      }`}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

/**
 * PracticeMatchGroup — one practice: a clickable header (avatar, bank · code,
 * what still needs work, match progress), then, when open, a stats strip and
 * its deposit ↔ bank rows.
 *
 *   rows:      every row for the practice (stats are computed from these)
 *   visibleRows: the rows the current filter shows
 *   stats:     practiceStats(rows)
 *   bankNote:  replaces the over/short line when the bank isn't connected
 */
export default function PracticeMatchGroup({
  name,
  bank,
  manager,
  rows = [],
  visibleRows = rows,
  stats,
  bankNote,
  open = true,
  onToggle,
  // Manual matching (omitted → read-only rows)
  canMatch = false,
  isSelected,
  onToggleLine,
  onUnmatch,
  unmatchingId = null,
  onAccept,
  acceptingId = null,
}) {
  const matchedCount = stats.matchedPairs;
  const needs = [
    stats.suggestedCount > 0 && `${stats.suggestedCount} suggested`,
    stats.variancePairs > 0 && plural(stats.variancePairs, "variance"),
    stats.depositPendingCount > 0 && `${stats.depositPendingCount} deposits pending`,
    stats.bankPendingCount > 0 && `${stats.bankPendingCount} bank pending`,
  ].filter(Boolean);
  const matchedPct = stats.deposits > 0 ? Math.round((stats.matched / stats.deposits) * 100) : 0;
  const diff = stats.bank - stats.deposits;

  return (
    <div className="border-b border-palette-slate-divider last:border-b-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full items-center gap-2.5 bg-palette-slate-soft px-4 py-2.5 text-left transition-colors hover:bg-palette-slate-muted"
      >
        <Chevron open={open} />
        <span
          className={`inline-flex h-6 w-6 flex-none select-none items-center justify-center rounded-lg text-[10px] font-semibold tracking-wide text-white ${avatarColor(name)}`}
        >
          {initials(name)}
        </span>
        <span className="min-w-0">
          <span className="block truncate text-[13.5px] font-semibold text-palette-slate-primary">
            {name}
          </span>
          <span className="block whitespace-nowrap text-[11.5px] text-palette-slate-placeholder">
            {[bank, manager].filter(Boolean).join(" · ")}
          </span>
        </span>
        <span className="flex-1" />
        {needs.length > 0 && (
          <span className="hidden whitespace-nowrap text-[12.5px] font-semibold text-palette-amber-primary md:inline">
            {needs.join(" · ")}
          </span>
        )}
        <span className="min-w-[96px] whitespace-nowrap text-right text-xs tabular-nums text-palette-slate-placeholder">
          {matchedCount} of {rows.length} matched
        </span>
      </button>

      {open && (
        <>
          <div className="flex flex-wrap items-stretch gap-x-6 border-t border-palette-slate-divider bg-white py-2.5 pl-[38px] pr-4">
            <Stat
              label="Deposits"
              value={formatCad(stats.deposits)}
              sub={plural(stats.depositCount, "line")}
            />
            <Stat
              label="In the bank"
              value={formatCad(stats.bank)}
              sub={plural(stats.bankCount, "transaction")}
            />
            <Stat
              label="Matched"
              tone="good"
              value={formatCad(stats.matched)}
              sub={`${plural(stats.matchedPairs, "pair")} · ${matchedPct}% of deposits`}
            />
            <Stat
              label="Suggested"
              tone={stats.suggestedCount ? "warn" : "default"}
              value={formatCad(stats.suggested)}
              sub={`${plural(stats.suggestedCount, "match", "matches")} to accept`}
            />
            <Stat
              label="Variances"
              tone={stats.variancePairs ? "warn" : "default"}
              value={formatSignedCad(stats.variance)}
              sub={`${plural(stats.variancePairs, "pair")} · ${formatCad(stats.varianceDeposited)} deposited`}
            />
            <Stat
              label="Deposits pending"
              tone={stats.depositPendingCount ? "warn" : "default"}
              value={formatCad(stats.depositPending)}
              sub={`${stats.depositPendingCount} not in the bank yet`}
            />
            <Stat
              label="Bank pending"
              tone={stats.bankPendingCount ? "warn" : "default"}
              value={formatCad(stats.bankPending)}
              sub={`${stats.bankPendingCount} without a deposit`}
            />
            <div
              className={`flex-[1_1_100%] pt-1.5 text-xs font-semibold ${
                bankNote || diff !== 0 ? "text-palette-amber-primary" : "text-palette-emerald-hover"
              }`}
            >
              {bankNote ||
                (diff === 0
                  ? "Bank and deposits agree"
                  : diff < 0
                    ? `Bank is ${formatCad(-diff)} short of deposits`
                    : `Bank is ${formatCad(diff)} over deposits`)}
            </div>
          </div>

          {visibleRows.length === 0 ? (
            <p className="m-0 border-t border-palette-slate-muted px-4 py-3 text-xs text-palette-slate-placeholder">
              Nothing in this view.
            </p>
          ) : (
            visibleRows.map((row) => (
              <DepositMatchRow
                key={row.id}
                {...row}
                manual={row.matchId != null && !row.auto}
                // Only unmatched lines can be picked for a new match.
                selectable={
                  canMatch && (row.status === "depositPending" || row.status === "bankPending")
                }
                isSelected={isSelected}
                onToggle={onToggleLine}
                onUnmatch={
                  canMatch && row.matchId != null ? () => onUnmatch?.(row.matchId) : undefined
                }
                unmatching={unmatchingId === row.matchId}
                onAccept={
                  canMatch && row.status === "suggested" ? () => onAccept?.(row) : undefined
                }
                accepting={acceptingId === row.id}
              />
            ))
          )}
        </>
      )}
    </div>
  );
}
